import { DELIVERY_OPTIONS } from "../constants/DeliveryConstants";

/**
 * The work day of an order: the day it is picked and packed (planning/werkdag-en-printstatus.md).
 *
 * That is not the delivery date. Since SooCool, a send order is collected at the warehouse on the
 * working day BEFORE its delivery, so Thursday's parcels are packed on Wednesday. Pickup orders are
 * picked on the day the customer collects them, Monday's send orders go with Trunkrs, which
 * collects on Monday itself (ordering for Monday stays open until Sunday 23:59), and since HVC-83
 * West-Friesland goes with our own van again on Wednesday to Friday, packed the same morning. So on
 * a Wednesday the warehouse works on Thursday's send orders, Wednesday's regional orders and
 * Wednesday's pickups:
 *
 * | Order                                        | Work day                                      |
 * |----------------------------------------------|-----------------------------------------------|
 * | pickup (VCE only; HVO has none)              | the pickup date itself, Saturday included     |
 * | send, regional, Wednesday to Friday (own van) | the delivery date itself                     |
 * | send, delivered Monday (Trunkrs)             | Monday itself                                 |
 * | send, delivered Tuesday to Saturday          | the weekday (Mon-Fri) before the delivery date |
 *
 * The send rule is the one `SooCoolOrderMapper.pickupDateFor` in api/ uses for the pickup task, so
 * the screen and the SooCool booking can never name a different day. Holidays are unknown to both.
 *
 * Dates are the DD-MM-YYYY strings stored in Firestore (`delivery.date`), parsed by hand: this app
 * does not reliably have dayjs' customParseFormat loaded outside the antd DatePicker. All arithmetic
 * is in UTC, so a daylight saving switch can never shift a date.
 *
 * Pure: no Firestore, no React.
 */

const DAY_MS = 24 * 60 * 60 * 1000;
const SUNDAY = 0;
const MONDAY = 1;
const SATURDAY = 6;

export const parseDate = (value) => {
  const match = /^(\d{2})-(\d{2})-(\d{4})$/.exec(value || "");
  if (!match) return null;
  const date = new Date(Date.UTC(Number(match[3]), Number(match[2]) - 1, Number(match[1])));
  // Reject 31-02-2026 and friends instead of letting Date roll them into March
  return date.getUTCDate() === Number(match[1]) && date.getUTCMonth() === Number(match[2]) - 1 ? date : null;
};

const pad = (number) => String(number).padStart(2, "0");

export const formatDate = (date) => `${pad(date.getUTCDate())}-${pad(date.getUTCMonth() + 1)}-${date.getUTCFullYear()}`;

export const addDays = (value, days) => {
  const date = typeof value === "string" ? parseDate(value) : value;
  if (!date) return null;
  return formatDate(new Date(date.getTime() + days * DAY_MS));
};

/** 0 = Sunday ... 6 = Saturday, like Date#getDay. Null for anything that is not a date. */
export const dayOfWeek = (value) => {
  const date = parseDate(value);
  return date === null ? null : date.getUTCDay();
};

/** Negative when `a` comes first, 0 when equal, positive when `a` comes later. Null on a bad date. */
export const compareDates = (a, b) => {
  const first = parseDate(a);
  const second = parseDate(b);
  if (!first || !second) return null;
  return first.getTime() - second.getTime();
};

/** Monday is the Trunkrs day; Tuesday to Saturday is SooCool's. */
export const isTrunkrsDeliveryDate = (deliveryDate) => dayOfWeek(deliveryDate) === MONDAY;

/** The weekday (Monday to Friday) before a date: what SooCool's pickup task is booked on. */
const previousWeekday = (value) => {
  let candidate = addDays(value, -1);
  while (candidate && (dayOfWeek(candidate) === SATURDAY || dayOfWeek(candidate) === SUNDAY)) {
    candidate = addDays(candidate, -1);
  }
  return candidate;
};

/**
 * The work day for a delivery date and type. Null when the date is not a date.
 *
 * An order without a known type falls back to its own date - the page's old delivery-date view -
 * so an odd order shows up on the day it always did instead of vanishing from every work day.
 */
export const workDayForDelivery = (deliveryDate, deliveryType) => {
  if (!parseDate(deliveryDate)) return null;
  if (deliveryType !== DELIVERY_OPTIONS.SEND) return deliveryDate;
  if (isTrunkrsDeliveryDate(deliveryDate)) return deliveryDate;
  return previousWeekday(deliveryDate);
};

// --- regional: our own van (HVC-83) -------------------------------------------------------------

/**
 * The days our own vans deliver West-Friesland (HVC-83, 2026-10-15): Wednesday to Friday, as before
 * SooCool. Must match `delivery.regional.days` in api/ and the regional calendar in the webshop.
 */
export const REGIONAL_DELIVERY_DAYS = [3, 4, 5];

/** A VCE send order to a West-Friesland address (`landelijk === false`). HVO has no regional customers. */
export const isRegionalSendOrder = (order) =>
  order?.shopSource === "VCE" && order?.landelijk === false && order?.delivery?.type === DELIVERY_OPTIONS.SEND;

/**
 * Delivered by our own van and planned in OptimoRoute: a regional send order on a van day. A regional
 * order that SooCool already has - placed and exported before HVC-83 - keeps the SooCool rules, and so
 * does one on another day (Tuesday or Saturday, also from before HVC-83): those are handled by hand.
 */
export const isRegionalOwnDelivery = (order) =>
  isRegionalSendOrder(order) &&
  REGIONAL_DELIVERY_DAYS.includes(dayOfWeek(order.delivery?.date)) &&
  !order?.soocool?.orderId;

export const workDayForOrder = (order) => {
  // Our own van leaves the warehouse in the afternoon of the delivery day itself
  if (isRegionalOwnDelivery(order)) return order.delivery.date;
  return workDayForDelivery(order?.delivery?.date, order?.delivery?.type);
};

/** True when this work day has a van round: a Wednesday, Thursday or Friday. */
export const isRegionalDeliveryDay = (day) => REGIONAL_DELIVERY_DAYS.includes(dayOfWeek(day));

export const isOnWorkDay = (order, workDay) => !!workDay && workDayForOrder(order) === workDay;

/**
 * The delivery dates of the send orders packed on this work day, in date order. Empty on a
 * weekend: nothing is sent then, only Saturday's pickups are picked.
 */
export const sendDeliveryDatesForWorkDay = (workDay) => {
  if (!parseDate(workDay)) return [];
  // A send order is packed on its own day (Monday, Trunkrs) or on the weekday before it, which is
  // at most two days back (Friday for a Sunday date - no such orders exist, but the rule covers it).
  return [0, 1, 2]
    .map((offset) => addDays(workDay, offset))
    .filter((date) => workDayForDelivery(date, DELIVERY_OPTIONS.SEND) === workDay);
};

/**
 * Every delivery date the orders query has to fetch for this work day: the day itself (its pickups,
 * and on Monday its Trunkrs orders) plus the send dates above.
 *
 * The day itself is always fetched, also when no order of it belongs to the work day: its send
 * orders are the ones the previous work day should have handled, which is how a left-behind order
 * is spotted (see `leftBehindSendOrders`).
 */
export const deliveryDatesForWorkDay = (workDay) => {
  if (!parseDate(workDay)) return [];
  return [...new Set([workDay, ...sendDeliveryDatesForWorkDay(workDay)])].sort((a, b) => compareDates(a, b));
};

/**
 * Send orders delivered on `workDay` that belonged to an earlier work day and never got a carrier.
 *
 * On a work-day screen they would otherwise be invisible: a Thursday delivery is Wednesday's work,
 * so on Thursday it is filtered out - while it still has to reach the customer today. `isBooked`
 * is the page's carrier check (getCarrierBooking), passed in so this file stays free of it.
 */
export const leftBehindSendOrders = (orders, workDay, isBooked) =>
  (orders || []).filter(
    (order) =>
      !!order &&
      order.delivery?.type === DELIVERY_OPTIONS.SEND &&
      order.delivery?.date === workDay &&
      workDayForOrder(order) !== workDay &&
      !order.containsOnlyGiftCards &&
      !isBooked(order),
  );
